// hooks/useEmrHook.js
import { useState, useEffect } from "react";
import { LaunchStage, logLaunchError } from "../utils/telemetry";

const EMR_USER_ID_SEPARATOR = "+";
const EMR_USER_ID_KEY = "emr_user_id";

// Equivalent to your patient_key array
const PATIENT_CONTEXT_KEYS = [
  "emr_patient_id",
  "emr_hospital_id",
  "fhir_patient_id",
  "emr_user_email",
  "emr_user_role",
  "emr_user_service_area",
  "emr_user_dept_speciality",
  "emr_dept_npi",
  "emr_dept_name",
  "emr_mode",
];

const NAME_KEYS = ["emr_user_first_name", "emr_user_last_name"];

interface EMRContext {
  emr_user_id: string;
  fhir_base_url: string;
  id_token: string;
  emr_patient_id?: string;
  emr_hospital_id?: string;
  fhir_patient_id?: string;
  emr_user_email?: string;
  emr_user_role?: string;
  emr_user_service_area?: string;
  emr_user_dept_speciality?: string;
  emr_dept_npi?: string;
  emr_dept_name?: string;
  emr_user_full_name?: string;
}

interface FHIRClient {
  state: {
    tokenResponse: Record<string, any>;
    serverUrl: string;
  };
  getPatientId: () => string;
  getUserId: () => string;
  getEncounterId: () => string;
}

export const useEmrHook = (
  fhirClient: FHIRClient | null
): { emrContext: EMRContext | null; error: any } => {
  const [emrContext, setEmrContext] = useState(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!fhirClient) return;

    const extractEMRContext = () => {
      try {
        const tokenResponse = fhirClient.state.tokenResponse;
        const getEMRUserId = (tokenResponse) => {
          const rawEmrUserId = tokenResponse[EMR_USER_ID_KEY];
          if (!rawEmrUserId) return null;

          return rawEmrUserId
            .replace(new RegExp(`^\\${EMR_USER_ID_SEPARATOR}`), "")
            .replace(new RegExp(`\\${EMR_USER_ID_SEPARATOR}`, "g"), " ")
            .trim();
        };

        const buildEMRContext = (tokenResponse) => {
          const context: any = {
            // Basic IDs
            emr_user_id: getEMRUserId(tokenResponse),
            fhir_base_url: fhirClient.state.serverUrl,
            id_token: tokenResponse.id_token,
          };

          PATIENT_CONTEXT_KEYS.forEach((key) => {
            if (tokenResponse[key]) {
              context[key] = tokenResponse[key];
            }
          });

          if (NAME_KEYS.every((key) => tokenResponse[key])) {
            context.emr_user_full_name = `${tokenResponse.emr_user_first_name} ${tokenResponse.emr_user_last_name}`;
          }

          return context;
        };
        const extractedContext = buildEMRContext(tokenResponse);
        setEmrContext(extractedContext);
      } catch (error) {
        logLaunchError(LaunchStage.CONTEXT_EXTRACTION, error, {
          failure: "emr_context_extraction_threw",
        });
        setError(error instanceof Error ? error.message : String(error));
      }
    };

    extractEMRContext();
  }, [fhirClient]);

  return { emrContext, error };
};
